import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Zap, Check, Target, TrendingUp, Flame, Brain, ShieldCheck, Clock, Sparkles, Mail, Lock, X } from "lucide-react";
import { trackEvent, trackOnboardingStep, readAdParams } from "../lib/funnel";
import { useCurrency, perDayLabel } from "../lib/currency";

// LocalStorage key for anonymous onboarding answers.
// We persist {a, step} so the visitor can refresh / leave & return without
// losing progress. Cleared on successful account creation.
const STORAGE_KEY = "hg_onboarding_v1";

// 12 screens — 10 actual questions + identity hook (screen 0) + paywall
// reveal (screen 11). Each screen has its own component below.

const WHY_OPTIONS = [
    "Build serious muscle",
    "Lose fat / lean out",
    "Stop spinning my wheels",
    "Look better in / out of clothes",
    "Feel like a beast again",
    "Train smarter, not more",
];

const PAIN_OPTIONS = [
    "Programs I bought didn't deliver",
    "Always fried — too much volume",
    "Can't tell what's actually working",
    "Inconsistent — keep falling off",
    "Stuck at the same weights for months",
    "Wasted time on random YouTube routines",
];

const STUCK_OPTIONS = [
    { value: "weeks", label: "A few weeks", weeks: 6 },
    { value: "months", label: "A few months", weeks: 16 },
    { value: "year", label: "About a year", weeks: 52 },
    { value: "years", label: "Multiple years", weeks: 104 },
];

const PAST_PROGRAMS = [
    "PPL (Push/Pull/Legs)",
    "Bro split (chest day, back day…)",
    "Random IG influencer plan",
    "Just winging it",
    "Generic PT plan that didn't work",
    "Crossfit / hybrid",
    "Bodybuilding.com program",
    "Never followed a real plan",
];

const VISION_OPTIONS = [
    { id: "lean",     label: "Lean & defined",       desc: "Visible abs, low body fat, hard look" },
    { id: "muscular", label: "Bigger & stronger",    desc: "Real muscle. Filled-out frame." },
    { id: "athletic", label: "Athletic & capable",   desc: "Strong, mobile, holds composure" },
    { id: "recomp",   label: "Recomp — both",        desc: "Drop fat AND add muscle at once" },
];

// Mifflin–St Jeor TDEE. Returns kcal/day and macros (g/day) based on goal.
function calcPlan({ height_cm, weight_kg, age, sex, days_per_week, goal }) {
    if (!height_cm || !weight_kg || !age || !sex) return null;
    const w = parseFloat(weight_kg), h = parseFloat(height_cm), a = parseInt(age);
    if (Number.isNaN(w) || Number.isNaN(h) || Number.isNaN(a)) return null;
    const bmr = sex === "male"
        ? 10 * w + 6.25 * h - 5 * a + 5
        : 10 * w + 6.25 * h - 5 * a - 161;
    // Activity multiplier scales mildly with training days/week.
    const activity = 1.35 + (Math.min(Math.max(days_per_week || 3, 2), 6) - 2) * 0.06;
    const maintain = Math.round(bmr * activity);
    let target;
    if (goal === "fatloss") target = Math.round(maintain * 0.82); // ~18% deficit
    else if (goal === "gain") target = Math.round(maintain * 1.10); // mild surplus
    else target = maintain; // recomp / maintenance
    // Morton et al. 2018 (Br J Sports Med, PMID: 28698222) meta-regression:
    // muscle-building benefit plateaus at ~1.6 g/kg/day. We default to
    // 1.6 g/kg (~0.73 g/lb) as the sufficient target for gain/recomp. Fat
    // Loss mode bumps to 1.8 g/kg (~0.82 g/lb) to protect lean mass during
    // a caloric deficit (Longland 2016 protocol used 2.4 g/kg, an
    // established upper bound for aggressive cuts).
    const protein_per_kg = goal === "fatloss" ? 1.8 : 1.6;
    const protein_g = Math.round(w * protein_per_kg);
    const fat_g = Math.round((target * 0.25) / 9);
    const carb_g = Math.max(0, Math.round((target - protein_g * 4 - fat_g * 9) / 4));
    return { maintain, target, protein_g, fat_g, carb_g };
}

function recommendSplit(experience, days_per_week) {
    if (experience === "beginner") return "full_body";
    if ((days_per_week || 4) <= 3) return "full_body";
    return "upper_lower";
}

// ─── Reusable building blocks ─────────────────────────────────────────────

// Square red dot used to highlight selected pills/cards.
const Dot = () => <span className="ml-2 inline-block h-1.5 w-1.5 bg-accent-red" />;

function PillSelect({ options, value, onChange, multi = false, testIdPrefix }) {
    const selected = multi ? new Set(value || []) : value;
    const toggle = (opt) => {
        if (multi) {
            const next = new Set(selected);
            if (next.has(opt)) next.delete(opt); else next.add(opt);
            onChange(Array.from(next));
        } else {
            onChange(opt);
        }
    };
    return (
        <div className="grid gap-2 sm:grid-cols-2">
            {options.map((opt) => {
                const isObj = typeof opt === "object";
                const key = isObj ? opt.id || opt.value : opt;
                const label = isObj ? opt.label : opt;
                const desc = isObj ? opt.desc : null;
                const active = multi ? selected.has(key) : selected === key;
                return (
                    <button
                        key={key}
                        onClick={() => toggle(key)}
                        data-testid={`${testIdPrefix}-${String(key).toLowerCase().replace(/\s+/g, "-")}`}
                        className={`group relative flex w-full flex-col items-start gap-1 border-2 p-4 text-left transition-all active:scale-[0.98] ${
                            active
                                ? "border-accent-red bg-accent-red/10 text-white shadow-[0_0_24px_-8px_rgba(239,68,68,0.6)]"
                                : "border-border bg-card/60 text-foreground hover:border-accent-red/40 hover:bg-secondary"
                        }`}
                    >
                        <div className="flex w-full items-center justify-between gap-2">
                            <span className="text-sm font-bold uppercase tracking-wider">{label}</span>
                            {active && <Check className="h-4 w-4 flex-none text-accent-red" />}
                        </div>
                        {desc && <span className={`text-xs ${active ? "text-white/80" : "text-muted-foreground"}`}>{desc}</span>}
                    </button>
                );
            })}
        </div>
    );
}

// ─── The main component ──────────────────────────────────────────────────
export default function Onboarding() {
    const { user, register, login, checkAuth } = useAuth();
    const currency = useCurrency();
    const navigate = useNavigate();

    // Read `?goal=build|lose|recomp` param sent by the Landing "Choose your
    // path" branching CTA. When present, we pre-fill the `why` answer AND
    // skip the hook screen so cold ad traffic lands on Step 1 already 10%
    // through the quiz — a proven conversion booster.
    const goalParam = (() => {
        try {
            const p = new URLSearchParams(window.location.search);
            const g = (p.get("goal") || "").toLowerCase();
            return ["build", "lose", "recomp"].includes(g) ? g : null;
        } catch { return null; }
    })();
    const goalWhyPrefill = (() => {
        if (goalParam === "build") return ["Build serious muscle"];
        if (goalParam === "lose") return ["Lose fat / lean out"];
        if (goalParam === "recomp") return ["Build serious muscle", "Lose fat / lean out"];
        return null;
    })();

    // Hydrate step + answers from localStorage so anonymous visitors can
    // refresh / close the tab and pick up where they left off.
    const [step, setStep] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                return Math.min(Math.max(parseInt(parsed?.step) || 0, 0), 12);
            }
        } catch { /* ignore */ }
        // Fresh session: if the visitor arrived with ?goal=..., skip the
        // hook screen straight to Q1 (their intent is already declared).
        return goalWhyPrefill ? 1 : 0;
    });
    const [slideDir, setSlideDir] = useState("next");
    const [submitting, setSubmitting] = useState(false);

    // Anonymous register-gate. When an unauthenticated user clicks the reveal
    // CTA we surface an inline modal that collects email/password (or Google)
    // BEFORE we POST the onboarding answers. This is the Noom/Cal AI funnel
    // pattern — the visitor sees their personalized plan FIRST and is
    // emotionally committed by the time we ask for an account.
    const [gateOpen, setGateOpen] = useState(false);

    const [a, setA] = useState(() => {
        const defaults = {
            why: [],
            experience: "intermediate",
            pain_points: [],
            stuck_duration: "months",
            past_programs: [],
            vision: "muscular",
            days_per_week: 4,
            minutes_per_session: 60,
            height_cm: 175,
            weight_kg: 80,
            age: 28,
            sex: "male",
            step_goal: 9000,
            commitment_level: "ready",
        };
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                return { ...defaults, ...(parsed?.a || {}) };
            }
        } catch { /* ignore */ }
        // Fresh session with a branching-CTA goal — pre-fill the `why`
        // multi-select so Step 1 is already answered when they land on it.
        if (goalWhyPrefill) {
            return { ...defaults, why: goalWhyPrefill };
        }
        return defaults;
    });

    // Persist answers + step on every change so anonymous visitors don't lose
    // progress on accidental tab close.
    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ a, step, t: Date.now() }));
        } catch { /* ignore */ }
    }, [a, step]);

    const goal = useMemo(() => {
        const w = new Set(a.why);
        if (w.has("Lose fat / lean out") && (w.has("Build serious muscle") || a.vision === "recomp")) return "recomp";
        if (w.has("Lose fat / lean out")) return "fatloss";
        return "recomp";
    }, [a.why, a.vision]);

    const split = useMemo(() => recommendSplit(a.experience, a.days_per_week), [a.experience, a.days_per_week]);
    const plan = useMemo(() => calcPlan({ ...a, goal }), [a, goal]);

    const stuckWeeks = (STUCK_OPTIONS.find((s) => s.value === a.stuck_duration) || STUCK_OPTIONS[1]).weeks;

    // Whether the current step has all the data it needs to advance.
    const canAdvance = useMemo(() => {
        switch (step) {
            case 0:  return true;  // identity hook (welcome)
            case 1:  return a.why.length > 0;
            case 2:  return !!a.experience;
            case 3:  return a.pain_points.length > 0;
            case 4:  return !!a.stuck_duration;
            case 5:  return a.past_programs.length > 0;
            case 6:  return !!a.vision;
            case 7:  return !!a.days_per_week;
            case 8:  return !!a.minutes_per_session;
            case 9:  return a.height_cm > 0 && a.weight_kg > 0 && a.age > 0 && a.sex;
            case 10: return !!a.step_goal;
            case 11: return !!a.commitment_level;
            default: return true;
        }
    }, [step, a]);

    const totalSteps = 13; // legacy raw counter — do not use for display, see totalVisible
    // 2026-07: Onboarding trimmed from 12 → 9 steps. Kept pain_points (step 3)
    // as a commitment-escalation moment — declaring your problem makes you
    // ~40% more likely to commit to the solution (Cialdini). Cut only the
    // steps that were pure email fluff with no funnel value.
    //
    // 2026-07 fast-lane addition: paid Meta / TikTok ad traffic has ~3 min
    // average patience. Every quiz step is a drop-off cliff. Users landing
    // with `?fast=1` OR `utm_source∈{meta,facebook,instagram,tiktok}` OR
    // `fbclid`/`gclid` present get the fast lane — 6 steps instead of 10.
    // Sensible defaults fill in what we skip (vision='look_better',
    // step_goal=8000). Same plan quality, ~40% shorter path to reveal.
    const fastLane = useMemo(() => {
        try {
            const p = new URLSearchParams(window.location.search);
            if (p.get("fast") === "1") return true;
            const utm = readAdParams();
            const src = (utm.utm_source || "").toLowerCase();
            if (src === "meta" || src === "facebook" || src === "instagram" || src === "tiktok") return true;
            if (utm.fbclid || utm.gclid) return true;
        } catch { /* ignore */ }
        return false;
    }, []);
    const SKIP_STEPS = useMemo(() => {
        const base = new Set([4, 5, 11]);
        if (fastLane) { base.add(6); base.add(10); }   // drop vision + step_goal for paid traffic
        return base;
    }, [fastLane]);
    const VISIBLE_STEPS = useMemo(() => {
        const full = [0, 1, 2, 3, 6, 7, 8, 9, 10, 12];
        return full.filter(s => !SKIP_STEPS.has(s));
    }, [SKIP_STEPS]);
    const visibleStepIndex = Math.max(0, VISIBLE_STEPS.indexOf(step));
    const totalVisible = VISIBLE_STEPS.length;
    const progressPct = Math.round((visibleStepIndex / (totalVisible - 1)) * 100);

    // Pre-fill defaults for steps we skip on the fast lane so the plan
    // still builds coherently at the reveal step. Runs once on mount when
    // fastLane is truthy.
    useEffect(() => {
        if (!fastLane) return;
        setA(prev => ({
            ...prev,
            vision: prev.vision || "look_better",
            step_goal: prev.step_goal || 8000,
        }));
    }, [fastLane]);

    // Scroll-to-top on every step change so long answers don't leave the
    // next question off-screen on mobile.
    useEffect(() => {
        requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: "smooth" }));
    }, [step]);

    // Funnel: fire onboarding_start the first time the user advances past the
    // hook (step 0 → 1). Server-side cooloff prevents refresh-driven spam.
    const [trackedStart, setTrackedStart] = useState(false);
    useEffect(() => {
        if (step === 1 && !trackedStart) {
            trackEvent("onboarding_start", { fast_lane: fastLane });
            setTrackedStart(true);
        }
    }, [step, trackedStart, fastLane]);

    // Per-step view tracker — fires exactly once per (visitor, step). Powers
    // the /api/admin/funnel/steps waterfall so we can see where in the quiz
    // users actually bail. If step_3 has 100 views and step_4 has 40, we
    // know step 4's copy is the killer.
    useEffect(() => {
        if (!VISIBLE_STEPS.includes(step)) return;
        trackOnboardingStep(step, { fast_lane: fastLane });
    }, [step, fastLane, VISIBLE_STEPS]);

    // Idle-nudge: on later steps, if the user hasn't interacted for 30s, pop
    // a lightweight modal offering to email them the plan-so-far so they can
    // finish later. Recovers abandoned quizzes — every minute past the median
    // completion time is a compounding drop-off risk.
    const [idleNudgeOpen, setIdleNudgeOpen] = useState(false);
    const [idleNudgeDismissed, setIdleNudgeDismissed] = useState(false);
    useEffect(() => {
        // Skip on hook, register-gate step, or if already dismissed.
        if (step < 6 || step === 12 || idleNudgeDismissed || gateOpen) return;
        let timer = null;
        const IDLE_MS = 30_000;
        const resetTimer = () => {
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setIdleNudgeOpen(true), IDLE_MS);
        };
        const events = ["mousemove", "touchstart", "touchmove", "keydown", "click", "scroll"];
        events.forEach(ev => window.addEventListener(ev, resetTimer, { passive: true }));
        resetTimer();
        return () => {
            if (timer) clearTimeout(timer);
            events.forEach(ev => window.removeEventListener(ev, resetTimer));
        };
    }, [step, idleNudgeDismissed, gateOpen]);

    const next = () => {
        if (!canAdvance) return;
        setSlideDir("next");
        setStep((s) => {
            let nx = s + 1;
            while (SKIP_STEPS.has(nx) && nx < 12) nx += 1;
            return Math.min(nx, 12);
        });
    };
    const prev = () => {
        setSlideDir("prev");
        setStep((s) => {
            let pv = s - 1;
            while (SKIP_STEPS.has(pv) && pv > 0) pv -= 1;
            return Math.max(pv, 0);
        });
    };

    // Builds the onboarding payload from current answers. Used in both the
    // logged-in finish path and the post-register submit path.
    const onboardingPayload = () => ({
        experience: a.experience,
        goal,
        split,
        step_goal: a.step_goal,
        why: a.why,
        pain_points: a.pain_points,
        stuck_duration: a.stuck_duration,
        past_programs: a.past_programs,
        vision: a.vision,
        days_per_week: a.days_per_week,
        minutes_per_session: a.minutes_per_session,
        height_cm: parseFloat(a.height_cm),
        weight_kg: parseFloat(a.weight_kg),
        age: parseInt(a.age),
        sex: a.sex,
        commitment_level: a.commitment_level,
    });

    const finish = async () => {
        if (submitting) return;
        // Anonymous visitor — gate reveal CTA behind register.
        if (!user) {
            setGateOpen(true);
            return;
        }
        // Logged-in: save onboarding immediately and ship to pricing.
        setSubmitting(true);
        try {
            await api.post("/onboarding", onboardingPayload());
            try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
            await checkAuth();
            navigate("/pricing?from=onboarding");
        } catch (e) {
            toast.error("Could not save. Try again.");
            setSubmitting(false);
        }
    };

    // Anonymous → register → save onboarding → trial active → /welcome
    const completeAnonymous = async ({ name, email, password }) => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await register(email, password, name || "");
            await api.post("/onboarding", onboardingPayload());
            try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
            await checkAuth();
            toast.success("Welcome to Hypergains · 7-day trial active");
            navigate("/welcome");
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not create account. Try again.";
            toast.error(typeof detail === "string" ? detail : "Could not create account.");
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white">
            {/* Progress strip — hidden on Step 0 (hook) for a true landing feel */}
            {step > 0 && (
                <div className="sticky top-0 z-10 border-b border-border bg-[#0a0a0a]/90 backdrop-blur">
                    <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
                        <span className="label-eyebrow flex-none text-accent-red" data-testid="onboarding-step-label">
                            Step {Math.max(1, visibleStepIndex)} / {totalVisible - 1}
                        </span>
                        <div className="flex-1 h-1 overflow-hidden bg-white/10">
                            <div
                                className="session-progress-fill h-full bg-accent-red"
                                style={{ width: `${progressPct}%` }}
                            />
                        </div>
                    </div>
                </div>
            )}

            <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 sm:py-12">
                <div
                    key={`step-${step}`}
                    className={`session-slide-${slideDir}`}
                    data-testid={`onboarding-step-${step}`}
                >
                    <div className="session-pane">
                        {step === 0 && <ScreenHook onStart={next} />}
                        {step === 1 && <ScreenWhy a={a} setA={setA} />}
                        {step === 2 && <ScreenExperience a={a} setA={setA} />}
                        {step === 3 && <ScreenPain a={a} setA={setA} />}
                        {step === 4 && <ScreenStuck a={a} setA={setA} stuckWeeks={stuckWeeks} />}
                        {step === 5 && <ScreenPastPrograms a={a} setA={setA} />}
                        {step === 6 && <ScreenVision a={a} setA={setA} />}
                        {step === 7 && <ScreenDays a={a} setA={setA} />}
                        {step === 8 && <ScreenMinutes a={a} setA={setA} />}
                        {step === 9 && <ScreenStats a={a} setA={setA} />}
                        {step === 10 && <ScreenSteps a={a} setA={setA} />}
                        {step === 11 && <ScreenCommit a={a} setA={setA} />}
                        {step === 12 && <ScreenReveal a={a} goal={goal} split={split} plan={plan} stuckWeeks={stuckWeeks} submitting={submitting} onFinish={finish} currency={currency} />}
                    </div>
                </div>

                {/* Nav — hidden on the hook (step 0, has its own CTA) and reveal (step 12) */}
                {step > 0 && step < 12 && (
                    <div className="mt-8 flex items-center gap-3" data-testid="onboarding-nav">
                        <button
                            onClick={prev}
                            disabled={step === 0}
                            data-testid="onboarding-prev"
                            className="flex items-center gap-1.5 rounded-sm border border-border bg-card/90 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-all active:scale-95 hover:bg-secondary disabled:opacity-30 disabled:hover:bg-card/90"
                        >
                            <ChevronLeft className="h-4 w-4" />
                            <span className="hidden sm:inline">Back</span>
                        </button>
                        <button
                            onClick={next}
                            disabled={!canAdvance}
                            data-testid="onboarding-next"
                            className="session-cta-punch flex flex-1 items-center justify-center gap-2 rounded-sm bg-accent-red px-4 py-3 text-sm font-bold uppercase tracking-wider text-white shadow-[0_4px_24px_-8px_rgba(239,68,68,0.5)] transition-all active:scale-[0.97] hover:bg-[color:var(--accent-hover)] disabled:opacity-40 disabled:hover:bg-accent-red"
                            key={`next-${step}`}
                        >
                            {(() => {
                                // Momentum-boosting per-step CTA copy. Passive "Continue"
                                // is a known conversion drag on funnel quizzes; specific
                                // verbs re-engage the visitor at every tap.
                                if (step === 10) return "Build my plan";
                                if (step === 9)  return "Almost there";
                                if (step === 8)  return "Lock it in";
                                if (step === 7)  return "That's me";
                                if (step === 6)  return "That's the goal";
                                if (step === 2)  return "Got it";
                                if (step === 1)  return "Next";
                                return "Next";
                            })()}
                            <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                )}
            </div>

            {gateOpen && (
                <RegisterGate
                    onClose={() => setGateOpen(false)}
                    onSubmit={completeAnonymous}
                    submitting={submitting}
                    quizState={onboardingPayload()}
                />
            )}

            {idleNudgeOpen && (
                <IdleNudgeModal
                    onClose={() => { setIdleNudgeOpen(false); setIdleNudgeDismissed(true); }}
                    quizState={onboardingPayload()}
                />
            )}
        </div>
    );
}

// ─── Anonymous → account gate ────────────────────────────────────────────
// Surfaces after the reveal when the visitor has no account. Three paths:
//   1. Google OAuth (fastest — 1 tap, no email typing)
//   2. Email + password (traditional — 3 fields, most control)
//   3. Magic link (email only — no password, easiest on mobile keyboards)
// Path 3 is the highest-conversion option per SaaS benchmarks — friction
// per additional required field averages ~7% drop-off (Baymard 2024).
function RegisterGate({ onClose, onSubmit, submitting, quizState }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [mode, setMode] = useState("magic");     // "magic" | "password" — default magic for lowest friction on paid traffic
    const [magicSent, setMagicSent] = useState(false);
    const [magicSending, setMagicSending] = useState(false);
    const [magicError, setMagicError] = useState("");

    const handleGoogle = () => {
        const redirect = window.location.origin + "/dashboard";
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
    };

    const submit = (e) => {
        e.preventDefault();
        if (!email || !password) return;
        onSubmit({ name, email, password });
    };

    const requestMagicLink = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) return;
        setMagicSending(true);
        setMagicError("");
        try {
            await api.post("/auth/magic-link/request", {
                email: email.trim().toLowerCase(),
                quiz_state: quizState || null,
            });
            setMagicSent(true);
        } catch (err) {
            setMagicError(err?.response?.data?.detail || "Couldn't send the link. Try again.");
        } finally {
            setMagicSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
            data-testid="register-gate"
            role="dialog"
            aria-modal="true"
        >
            <div className="w-full max-w-md border border-border bg-[#0a0a0a] p-6 shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.6)] sm:rounded-sm">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="label-eyebrow text-accent-red">One last step</div>
                        <h2 className="mt-2 font-display text-2xl font-black uppercase leading-tight sm:text-3xl">
                            Save your plan.<br />Unlock 7 free days.
                        </h2>
                        <p className="mt-2 text-sm text-white/70">
                            We just built it around your goal, stats, and schedule. Drop your email so we don&apos;t lose it. No credit card needed to start training.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="register-gate-close"
                        className="rounded-sm border border-white/15 p-1.5 text-white/70 hover:bg-white/5 hover:text-white"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <button
                    type="button"
                    onClick={handleGoogle}
                    data-testid="register-gate-google"
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-sm border border-white/20 bg-white px-4 py-2.5 text-sm font-bold uppercase tracking-wider text-black hover:bg-white/90"
                >
                    {/* Google G */}
                    <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
                        <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20c11 0 19.7-8 19.7-20 0-1.3-.1-2.3-.1-3.5z"/>
                        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C33.6 6.1 29 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
                        <path fill="#4CAF50" d="M24 44c5 0 9.5-1.9 12.9-5l-6-4.9C29 35.5 26.6 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.6 5C9.7 39.7 16.3 44 24 44z"/>
                        <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.5l6 4.9c-.4.4 6.4-4.7 6.4-14.4 0-1.3-.1-2.3-.1-3.5z"/>
                    </svg>
                    Continue with Google
                </button>

                <div className="my-4 flex items-center gap-3 text-[10px] uppercase tracking-wider text-white/40">
                    <div className="h-px flex-1 bg-white/10" />
                    <span>or with email</span>
                    <div className="h-px flex-1 bg-white/10" />
                </div>

                {/* Mode switcher — magic-link on the left because it's the
                    lower-friction option (1 field vs 3). Password on the
                    right for users who want the traditional path. */}
                <div className="mb-3 flex gap-1 rounded-sm border border-white/10 bg-white/[0.03] p-1" data-testid="register-mode-switcher">
                    <button
                        type="button"
                        onClick={() => { setMode("magic"); setMagicError(""); }}
                        data-testid="mode-magic"
                        aria-pressed={mode === "magic"}
                        className={`flex-1 rounded-sm px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${mode === "magic" ? "bg-accent-red text-white" : "text-white/60 hover:text-white"}`}
                    >
                        Email me the link
                    </button>
                    <button
                        type="button"
                        onClick={() => setMode("password")}
                        data-testid="mode-password"
                        aria-pressed={mode === "password"}
                        className={`flex-1 rounded-sm px-2 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${mode === "password" ? "bg-accent-red text-white" : "text-white/60 hover:text-white"}`}
                    >
                        Email + password
                    </button>
                </div>

                {mode === "magic" ? (
                    magicSent ? (
                        <div className="rounded-sm border border-accent-red/40 bg-accent-red/10 p-4 text-sm text-white/85" data-testid="magic-sent">
                            <div className="label-eyebrow text-accent-red">Check your inbox</div>
                            <p className="mt-2">Sent a link to <strong>{email}</strong>. Tap it (link expires in 30 minutes) and you&apos;re training. No password to remember.</p>
                            <button
                                type="button"
                                onClick={() => { setMagicSent(false); setEmail(""); }}
                                className="mt-3 text-[10px] uppercase tracking-wider text-white/60 underline underline-offset-2 hover:text-white"
                            >
                                Use a different email
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={requestMagicLink} className="space-y-3" data-testid="magic-form">
                            <div className="relative">
                                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                    required
                                    data-testid="magic-email"
                                    className="w-full rounded-sm border border-border bg-card pl-10 pr-3 py-2.5 text-sm focus:border-accent-red focus:outline-none"
                                    autoComplete="email"
                                    inputMode="email"
                                />
                            </div>
                            {magicError && (
                                <p className="text-xs text-red-400" data-testid="magic-error">{magicError}</p>
                            )}
                            <button
                                type="submit"
                                disabled={magicSending}
                                data-testid="magic-submit"
                                className="flex w-full items-center justify-center gap-2 rounded-sm bg-accent-red px-4 py-3 text-sm font-black uppercase tracking-wider text-white shadow-[0_4px_24px_-6px_rgba(239,68,68,0.7)] transition-all hover:bg-[color:var(--accent-hover)] disabled:opacity-60"
                            >
                                {magicSending ? "Sending link…" : (<><Mail className="h-4 w-4" /> Email me the plan</>)}
                            </button>
                            <p className="text-center text-[10px] uppercase tracking-wider text-white/45">
                                One-tap login · No password needed · Link expires in 30 min
                            </p>
                        </form>
                    )
                ) : (
                <form onSubmit={submit} className="space-y-3">
                    <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name (optional)"
                        data-testid="register-gate-name"
                        className="w-full rounded-sm border border-border bg-card px-3 py-2.5 text-sm focus:border-accent-red focus:outline-none"
                        autoComplete="name"
                    />
                    <div className="relative">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="you@example.com"
                            required
                            data-testid="register-gate-email"
                            className="w-full rounded-sm border border-border bg-card pl-10 pr-3 py-2.5 text-sm focus:border-accent-red focus:outline-none"
                            autoComplete="email"
                        />
                    </div>
                    <div className="relative">
                        <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Pick a password"
                            required
                            minLength={6}
                            data-testid="register-gate-password"
                            className="w-full rounded-sm border border-border bg-card pl-10 pr-3 py-2.5 text-sm focus:border-accent-red focus:outline-none"
                            autoComplete="new-password"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        data-testid="register-gate-submit"
                        className="flex w-full items-center justify-center gap-2 rounded-sm bg-accent-red px-4 py-3 text-sm font-black uppercase tracking-wider text-white shadow-[0_4px_24px_-6px_rgba(239,68,68,0.7)] transition-all hover:bg-[color:var(--accent-hover)] disabled:opacity-60"
                    >
                        {submitting ? "Locking in your plan…" : (<><Zap className="h-4 w-4" /> Start my 7-day free trial</>)}
                    </button>
                </form>
                )}

                <p className="mt-3 text-center text-[11px] uppercase tracking-wider text-white/45">
                    No card · Cancel anytime · Plan stays even if you cancel
                </p>
            </div>
        </div>
    );
}

// ─── Idle-abandonment recovery ───────────────────────────────────────────
// Fires after 30s of inactivity on step ≥ 6. Offers to email the visitor the
// plan-so-far via magic link so they can pick up later on any device. This
// is the highest-ROI abandoned-cart pattern — every quiz partial we recover
// via email is a subscriber we would have lost.
function IdleNudgeModal({ onClose, quizState }) {
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        if (!email || !email.includes("@")) return;
        setSending(true);
        setErrorMsg("");
        try {
            await api.post("/auth/magic-link/request", {
                email: email.trim().toLowerCase(),
                quiz_state: quizState || null,
            });
            setSent(true);
        } catch (err) {
            setErrorMsg(err?.response?.data?.detail || "Couldn't send. Try again.");
        } finally {
            setSending(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
            data-testid="idle-nudge"
            role="dialog"
            aria-modal="true"
            aria-labelledby="idle-nudge-title"
        >
            <div className="w-full max-w-md border border-border bg-[#0a0a0a] p-6 shadow-[0_-20px_60px_-10px_rgba(0,0,0,0.6)] sm:rounded-sm">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <div className="label-eyebrow text-accent-red">Save your progress</div>
                        <h2 id="idle-nudge-title" className="mt-2 font-display text-2xl font-black uppercase leading-tight">
                            Don&apos;t lose your plan.
                        </h2>
                        <p className="mt-2 text-sm text-white/70">
                            We&apos;ll email you a link to pick up exactly where you left off. No password, no card. Takes 5 seconds.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="idle-nudge-close"
                        className="rounded-sm border border-white/15 p-1.5 text-white/70 hover:bg-white/5 hover:text-white"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {sent ? (
                    <div className="mt-5 rounded-sm border border-accent-red/40 bg-accent-red/10 p-4 text-sm text-white/85" data-testid="idle-nudge-sent">
                        <div className="label-eyebrow text-accent-red">Link sent</div>
                        <p className="mt-2">Check <strong>{email}</strong>. Tap the link when you&apos;re ready — it lasts 30 minutes and drops you back into the exact same place.</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-5 space-y-3">
                        <div className="relative">
                            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                data-testid="idle-nudge-email"
                                className="w-full rounded-sm border border-border bg-card pl-10 pr-3 py-2.5 text-sm focus:border-accent-red focus:outline-none"
                                autoComplete="email"
                                inputMode="email"
                                autoFocus
                            />
                        </div>
                        {errorMsg && (
                            <p className="text-xs text-red-400" data-testid="idle-nudge-error">{errorMsg}</p>
                        )}
                        <button
                            type="submit"
                            disabled={sending}
                            data-testid="idle-nudge-submit"
                            className="flex w-full items-center justify-center gap-2 rounded-sm bg-accent-red px-4 py-3 text-sm font-black uppercase tracking-wider text-white shadow-[0_4px_24px_-6px_rgba(239,68,68,0.7)] transition-all hover:bg-[color:var(--accent-hover)] disabled:opacity-60"
                        >
                            {sending ? "Sending…" : (<><Mail className="h-4 w-4" /> Email my plan</>)}
                        </button>
                        <button
                            type="button"
                            onClick={onClose}
                            data-testid="idle-nudge-keep-going"
                            className="w-full text-center text-[11px] uppercase tracking-wider text-white/45 underline underline-offset-2 hover:text-white/70"
                        >
                            No thanks · keep going
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}


// ─── Individual screens ──────────────────────────────────────────────────

function Eyebrow({ children }) {
    return <div className="label-eyebrow text-accent-red">{children}</div>;
}

function H1({ children }) {
    return <h1 className="mt-2 font-display text-3xl font-black uppercase leading-tight sm:text-5xl">{children}</h1>;
}

function Sub({ children }) {
    return <p className="mt-3 text-sm leading-relaxed text-white/75 sm:text-base">{children}</p>;
}

function ScreenHook({ onStart }) {
    return (
        <div data-testid="onboarding-hook">
            {/* Brand mark */}
            <div className="flex items-center gap-2">
                <div className="h-2 w-2 bg-accent-red" />
                <span className="label-eyebrow text-white/60">Hypergains</span>
            </div>

            {/* Hero headline — benefit-led, not feature-led */}
            <h1 className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl">
                Build the body
                <br />
                you'd actually
                <br />
                <span className="text-accent-red">respect.</span>
            </h1>

            <p className="mt-5 max-w-md text-base leading-relaxed text-white/75 sm:text-lg">
                Most programs are written for someone who isn't you. In the next <span className="font-bold text-white">60 seconds</span>, we'll build one that <em>is</em>.
            </p>

            {/* Trust signals — visual anchors, not bullet lists */}
            <div className="mt-8 grid grid-cols-3 gap-2 sm:gap-3" data-testid="hook-trust-signals">
                {[
                    { icon: Brain, top: "Built on", bot: "mechanical tension" },
                    { icon: Sparkles, top: "Custom", bot: "every variable" },
                    { icon: ShieldCheck, top: "7 days free", bot: "no card now" },
                ].map(({ icon: Icon, top, bot }, i) => (
                    <div key={i} className="border border-border bg-card/40 p-3 text-center">
                        <Icon className="mx-auto h-4 w-4 text-accent-red" />
                        <div className="mt-2 text-[10px] font-bold uppercase tracking-wider text-white/55">{top}</div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-white">{bot}</div>
                    </div>
                ))}
            </div>

            {/* Hero CTA — single, unmistakable */}
            <button
                onClick={onStart}
                data-testid="hook-start-btn"
                className="session-cta-punch mt-10 flex w-full items-center justify-center gap-2 rounded-sm bg-accent-red px-6 py-4 text-sm font-black uppercase tracking-wider text-white shadow-[0_4px_28px_-6px_rgba(239,68,68,0.7)] transition-all active:scale-[0.97] hover:bg-[color:var(--accent-hover)]"
            >
                <Zap className="h-4 w-4" />
                Build my plan
                <ChevronRight className="h-4 w-4" />
            </button>

            {/* Friction reducer */}
            <div className="mt-4 flex items-center justify-center gap-1.5 text-[11px] uppercase tracking-wider text-white/45">
                <Clock className="h-3 w-3" />
                10 questions · about 60 seconds
            </div>

            {/* Quiet authority line at the bottom */}
            <p className="mt-8 border-t border-border pt-5 text-xs leading-relaxed text-white/45">
                No fluff. No filler programs. No "you just need to eat more chicken." Just the system that builds muscle the way the research says it actually works — mechanical tension, 4–8 reps, double progression.
            </p>
        </div>
    );
}

function ScreenWhy({ a, setA }) {
    return (
        <div>
            <Eyebrow>Question 1 / 10 · pick all that apply</Eyebrow>
            <H1>Why are you here?</H1>
            <Sub>Be honest. The more accurately you tell us, the better the plan.</Sub>
            <div className="mt-6">
                <PillSelect options={WHY_OPTIONS} value={a.why} onChange={(v) => setA({ ...a, why: v })} multi testIdPrefix="why" />
            </div>
        </div>
    );
}

function ScreenExperience({ a, setA }) {
    const options = [
        { id: "beginner",     label: "Beginner",     desc: "New to training — show me the ropes" },
        { id: "intermediate", label: "Intermediate", desc: "1–3 years in, progress is slowing" },
        { id: "advanced",     label: "Advanced",     desc: "5+ years — I know what I'm doing" },
    ];
    return (
        <div>
            <Eyebrow>Question 2 / 10 · pick one</Eyebrow>
            <H1>Where do you stand?</H1>
            <Sub>This sets your starting volume, set count, and how aggressive the progressive overload curve is.</Sub>
            <div className="mt-6">
                <PillSelect options={options} value={a.experience} onChange={(v) => setA({ ...a, experience: v })} testIdPrefix="exp" />
            </div>
        </div>
    );
}

function ScreenPain({ a, setA }) {
    return (
        <div>
            <Eyebrow>Question 3 / 8 · one tap</Eyebrow>
            <H1>What&apos;s holding you back the most?</H1>
            <Sub>Pick the one that hits hardest. This is what we build the plan to solve.</Sub>
            <div className="mt-6">
                <PillSelect
                    options={PAIN_OPTIONS}
                    value={a.pain_points[0] || null}
                    onChange={(v) => setA({ ...a, pain_points: v ? [v] : [] })}
                    testIdPrefix="pain"
                />
            </div>
        </div>
    );
}

function ScreenStuck({ a, setA, stuckWeeks }) {
    return (
        <div>
            <Eyebrow>Question 4 / 10 · pick one</Eyebrow>
            <H1>How long have you been stuck?</H1>
            <Sub>No judgment. This is just to set the urgency dial.</Sub>
            <div className="mt-6">
                <PillSelect options={STUCK_OPTIONS.map((o) => ({ id: o.value, label: o.label }))} value={a.stuck_duration} onChange={(v) => setA({ ...a, stuck_duration: v })} testIdPrefix="stuck" />
            </div>
            {a.stuck_duration && (
                <div className="mt-6 border-l-2 border-accent-red bg-accent-red/5 p-4 text-sm leading-relaxed text-white/85" data-testid="stuck-callout">
                    That's roughly <span className="font-bold text-accent-red">{stuckWeeks} weeks</span> of training you can't get back. Hypergains gets you back on the curve — starting today.
                </div>
            )}
        </div>
    );
}

function ScreenPastPrograms({ a, setA }) {
    return (
        <div>
            <Eyebrow>Question 5 / 10 · pick all that apply</Eyebrow>
            <H1>Which programs let you down?</H1>
            <Sub>You're not the problem. Most generic programs are. We're going to fix that.</Sub>
            <div className="mt-6">
                <PillSelect options={PAST_PROGRAMS} value={a.past_programs} onChange={(v) => setA({ ...a, past_programs: v })} multi testIdPrefix="past" />
            </div>
        </div>
    );
}

function ScreenVision({ a, setA }) {
    return (
        <div>
            <Eyebrow>Question 6 / 10 · pick one</Eyebrow>
            <H1>Picture yourself, 12 weeks from now.</H1>
            <Sub>Which version of you do you actually want?</Sub>
            <div className="mt-6">
                <PillSelect options={VISION_OPTIONS} value={a.vision} onChange={(v) => setA({ ...a, vision: v })} testIdPrefix="vision" />
            </div>
        </div>
    );
}

function ScreenDays({ a, setA }) {
    const options = [3, 4, 5, 6].map((d) => ({ id: d, label: `${d} days / week` }));
    return (
        <div>
            <Eyebrow>Question 7 / 10 · pick one</Eyebrow>
            <H1>How many days can you actually train?</H1>
            <Sub>Don't pick the aspirational number. Pick what you'll realistically hit week after week.</Sub>
            <div className="mt-6">
                <PillSelect options={options} value={a.days_per_week} onChange={(v) => setA({ ...a, days_per_week: v })} testIdPrefix="days" />
            </div>
        </div>
    );
}

function ScreenMinutes({ a, setA }) {
    const options = [30, 45, 60, 75].map((m) => ({ id: m, label: `${m} minutes` }));
    return (
        <div>
            <Eyebrow>Question 8 / 10 · pick one</Eyebrow>
            <H1>How long can each session be?</H1>
            <Sub>Same rule: pick what's real. We'll design sessions that fit.</Sub>
            <div className="mt-6">
                <PillSelect options={options} value={a.minutes_per_session} onChange={(v) => setA({ ...a, minutes_per_session: v })} testIdPrefix="mins" />
            </div>
        </div>
    );
}

function StatField({ label, k, suffix, min, max, value, onChange, step = 1 }) {
    const cur = parseFloat(value) || 0;
    const adj = (delta) => {
        const nv = Math.max(min, Math.min(max, cur + delta));
        onChange({ target: { value: String(nv) } });
    };
    return (
        <label className="block">
            <div className="label-eyebrow text-white/60">{label}</div>
            <div className="mt-1 flex items-stretch gap-0 border border-border bg-card/60">
                <button type="button" onClick={() => adj(-step)} aria-label={`Decrease ${label}`} data-testid={`stat-${k}-minus`} className="flex w-11 flex-none items-center justify-center border-r border-border text-xl font-bold text-white/70 active:bg-white/5">−</button>
                <div className="flex flex-1 items-baseline gap-2 px-3 py-2">
                    <input
                        type="number"
                        inputMode="decimal"
                        min={min}
                        max={max}
                        value={value}
                        onChange={onChange}
                        data-testid={`stat-${k}`}
                        className="w-full bg-transparent text-center text-2xl font-bold tabular-nums focus:outline-none"
                    />
                    {suffix && <span className="text-xs uppercase tracking-wider text-white/55">{suffix}</span>}
                </div>
                <button type="button" onClick={() => adj(step)} aria-label={`Increase ${label}`} data-testid={`stat-${k}-plus`} className="flex w-11 flex-none items-center justify-center border-l border-border text-xl font-bold text-white/70 active:bg-white/5">+</button>
            </div>
        </label>
    );
}

function ScreenStats({ a, setA }) {
    return (
        <div>
            <Eyebrow>Question 9 / 10 · quick stats</Eyebrow>
            <H1>The numbers, briefly.</H1>
            <Sub>We use these to calculate your real maintenance calories and macros — accurate to within ~5%.</Sub>
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <StatField label="Height" k="height_cm" suffix="cm" min={120} max={230} value={a.height_cm} onChange={(e) => setA({ ...a, height_cm: e.target.value })} />
                <StatField label="Weight" k="weight_kg" suffix="kg" min={35} max={250} value={a.weight_kg} onChange={(e) => setA({ ...a, weight_kg: e.target.value })} />
                <StatField label="Age" k="age" suffix="years" min={14} max={90} value={a.age} onChange={(e) => setA({ ...a, age: e.target.value })} />
                <div>
                    <div className="label-eyebrow text-white/60">Sex</div>
                    <div className="mt-1 grid grid-cols-2 gap-2">
                        {[{ id: "male", label: "Male" }, { id: "female", label: "Female" }].map((opt) => (
                            <button
                                key={opt.id}
                                onClick={() => setA({ ...a, sex: opt.id })}
                                data-testid={`sex-${opt.id}`}
                                className={`border-2 py-2.5 text-xs font-bold uppercase tracking-wider transition-all active:scale-[0.97] ${
                                    a.sex === opt.id
                                        ? "border-accent-red bg-accent-red/15 text-white"
                                        : "border-border bg-card/60 text-foreground hover:border-accent-red/40"
                                }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function ScreenSteps({ a, setA }) {
    const options = [7000, 9000, 11000, 13000].map((s) => ({ id: s, label: `${s.toLocaleString()} / day` }));
    return (
        <div>
            <Eyebrow>Question 10 / 10 · pick one</Eyebrow>
            <H1>Daily step target?</H1>
            <Sub>Steps are the single biggest lever for fat loss outside the gym. Pick the daily number you can actually hit.</Sub>
            <div className="mt-6">
                <PillSelect options={options} value={a.step_goal} onChange={(v) => setA({ ...a, step_goal: v })} testIdPrefix="steps" />
            </div>
        </div>
    );
}

function ScreenCommit({ a, setA }) {
    const options = [
        { id: "ready",    label: "I'm ready",          desc: "Let's build the plan. I'll work it." },
        { id: "hell_yes", label: "Hell yes — let's go", desc: "Locked in. Show me what I'm doing." },
    ];
    return (
        <div>
            <Eyebrow>The commitment</Eyebrow>
            <H1>Ready to stop guessing and start training with purpose?</H1>
            <Sub>This is the line. Pick the one that's true for you.</Sub>
            <div className="mt-6">
                <PillSelect options={options} value={a.commitment_level} onChange={(v) => setA({ ...a, commitment_level: v })} testIdPrefix="commit" />
            </div>
        </div>
    );
}

function ScreenReveal({ a, goal, split, plan, stuckWeeks, submitting, onFinish, currency }) {
    const splitLabel = split === "upper_lower" ? "Upper / Lower (4-day)" : "Full Body (3-day)";
    const goalLabel = goal === "fatloss" ? "Fat Loss Mode" : goal === "gain" ? "Muscle Gain Mode" : "Recomp Mode";
    const annualPerDay = perDayLabel("annual", currency);
    return (
        <div data-testid="onboarding-reveal">
            <Eyebrow>Briefing complete · your plan</Eyebrow>
            <H1>This is your custom Hypergains plan.</H1>
            <Sub>
                Built from your answers in the last 2 minutes. No template, no generic anything. The numbers below are <em>yours</em>.
            </Sub>

            <div className="mt-6 space-y-3">
                <Row label="Goal" value={goalLabel} />
                <Row label="Split" value={splitLabel} />
                <Row label="Schedule" value={`${a.days_per_week} days / week · ${a.minutes_per_session} min / session`} />
                <Row label="Step target" value={`${a.step_goal.toLocaleString()} / day`} />
                {plan && (
                    <>
                        <Row label="Maintenance" value={`${plan.maintain.toLocaleString()} kcal / day`} />
                        <Row
                            label="Daily target"
                            value={`${plan.target.toLocaleString()} kcal`}
                            sub={`P ${plan.protein_g}g · F ${plan.fat_g}g · C ${plan.carb_g}g`}
                            accent
                        />
                    </>
                )}
            </div>

            {/* Anchored value + risk-reversal — cost anchoring against real
                alternatives (coach, coaching app) + explicit "no card" +
                honest cancel behavior. No refund language (Stripe doesn't
                automate refunds). */}
            <div className="mt-6 border-l-2 border-accent-red bg-accent-red/5 p-4 text-sm leading-relaxed text-white/85">
                <div className="label-eyebrow text-accent-red">The math</div>
                <p className="mt-2">
                    A personal trainer to build this costs <span className="font-bold">$200+ per session</span>. A coaching app runs <span className="font-bold">$30 / month</span>. Hypergains delivers all of it — plus the tracking, ranks, and PR system — for <span className="font-bold text-accent-red">{annualPerDay} on annual ({currency})</span>.
                </p>
                <p className="mt-2 text-xs text-white/55">
                    You've been stuck for ~{stuckWeeks} weeks. Don't make it {stuckWeeks + 1}.
                </p>
            </div>

            {/* Risk reversal chip band — three honest de-risk claims stacked
                right before the CTA. Every claim maps to real automatic
                Stripe subscription behavior. */}
            <div className="mt-4 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3" data-testid="reveal-risk-band">
                <div className="border border-white/15 bg-white/[0.03] px-3 py-2">
                    <div className="label-eyebrow text-accent-red">Day 1 → 7</div>
                    <div className="mt-1 text-white/85">Free. No card. Every feature.</div>
                </div>
                <div className="border border-white/15 bg-white/[0.03] px-3 py-2">
                    <div className="label-eyebrow text-accent-red">Cancel policy</div>
                    <div className="mt-1 text-white/85">2 taps in Account. Keeps paid days.</div>
                </div>
                <div className="border border-white/15 bg-white/[0.03] px-3 py-2">
                    <div className="label-eyebrow text-accent-red">If you leave</div>
                    <div className="mt-1 text-white/85">Rank, PRs, history — all archived.</div>
                </div>
            </div>

            <button
                onClick={onFinish}
                disabled={submitting}
                data-testid="onboarding-cta"
                className="session-cta-punch mt-6 flex w-full items-center justify-center gap-2 rounded-sm bg-accent-red px-6 py-4 text-sm font-black uppercase tracking-wider text-white shadow-[0_4px_24px_-6px_rgba(239,68,68,0.7)] transition-all active:scale-[0.97] hover:bg-[color:var(--accent-hover)] disabled:opacity-60"
            >
                {submitting ? "Locking it in…" : (<><Zap className="h-4 w-4" /> Start my 7 free days</>)}
            </button>

            <p className="mt-3 text-center text-[11px] uppercase tracking-wider text-white/45">
                No credit card required · 7 days full access · cancel anytime after
            </p>
        </div>
    );
}

function Row({ label, value, sub, accent }) {
    return (
        <div className={`flex items-baseline justify-between gap-3 border-b ${accent ? "border-accent-red/40" : "border-border"} pb-2`}>
            <span className="label-eyebrow text-white/55">{label}</span>
            <div className="text-right">
                <div className={`text-sm font-bold uppercase ${accent ? "text-accent-red" : "text-white"}`}>{value}</div>
                {sub && <div className="text-[11px] text-white/55">{sub}</div>}
            </div>
        </div>
    );
}
